import { useState } from "react";
import { arrayMove } from "@tellescope/utils";
import { useStripeAccounts } from "./useStripeAccounts";
import { useShopStripeProducts } from "./useShopStripeProducts";
import { useShopStripePrices } from "./useShopStripePrices";
import { useResolveTellescopeProducts } from "./useResolveTellescopeProducts";
import { buildSelectedShopVariant, buildSelectedShopProductFields } from "./buildSelectedShopEntry";
import type { SelectedShopEntry } from "./types";
import type { StripeShopProduct, StripeShopPrice } from "./stripeShopTypes";
import type { ShopProduct } from "../enduser/types";
import type { VisibilityCondition } from "@tellescope/visibility-schema";
import { SHOP_MAX_QUANTITY } from "@tellescope/shop-schema";

export type QuantitySupport = "allowed" | "not-allowed" | "unknown";

export interface UseShopBlockEditorLogicOptions {
  initialSelection: SelectedShopEntry[];
  initialStripeKey?: string | null;
}

export const useShopBlockEditorLogic = ({
  initialSelection,
  initialStripeKey = null,
}: UseShopBlockEditorLogicOptions) => {
  const [stripeKey, setStripeKeyState] = useState<string | null>(initialStripeKey);
  const [selection, setSelection] = useState<SelectedShopEntry[]>(initialSelection);
  const [drillDownProductId, setDrillDownProductId] = useState<string | null>(null);

  // Every product in a block's selection must come from the same Stripe account - mixing accounts
  // risks cross-account customer ID confusion at checkout (one customer record per account).
  // Switching accounts mid-edit clears the current selection rather than leaving stale entries
  // from the old account mixed in with new ones.
  const setStripeKey = (nextStripeKey: string) => {
    setStripeKeyState(nextStripeKey);
    setSelection([]);
  };

  const { accounts, isLoading: accountsLoading, error: accountsError } = useStripeAccounts();
  const productsQuery = useShopStripeProducts(stripeKey);
  const pricesQuery = useShopStripePrices(stripeKey, drillDownProductId);

  const selectedStripePriceIds = selection.flatMap((entry) => entry.variants.map((v) => v.stripePriceId));
  const {
    productByStripePriceId,
    isLoading: resolutionLoading,
    hasChecked: resolutionChecked,
    hasCheckedPriceId,
  } = useResolveTellescopeProducts(selectedStripePriceIds);

  const isPriceSelected = (stripePriceId: string) =>
    selection.some((entry) => entry.variants.some((v) => v.stripePriceId === stripePriceId));

  const togglePrice = (product: StripeShopProduct, price: StripeShopPrice) => {
    setSelection((prev) => {
      const existing = prev.find((entry) => entry.stripeProductId === product.id);

      if (!existing) {
        const newEntry: SelectedShopEntry = {
          ...buildSelectedShopProductFields(product),
          variants: [buildSelectedShopVariant(price)],
          recommended: false,
        };
        return [...prev, newEntry];
      }

      const hasVariant = existing.variants.some((v) => v.stripePriceId === price.id);
      const nextVariants = hasVariant
        ? existing.variants.filter((v) => v.stripePriceId !== price.id)
        : [...existing.variants, buildSelectedShopVariant(price)];

      if (nextVariants.length === 0) {
        return prev.filter((entry) => entry.stripeProductId !== product.id);
      }

      return prev.map((entry) => (entry.stripeProductId === product.id ? { ...entry, variants: nextVariants } : entry));
    });
  };

  const removeSelection = (stripeProductId: string) => {
    setSelection((prev) => prev.filter((entry) => entry.stripeProductId !== stripeProductId));
  };

  const reorderSelection = (fromIndex: number, toIndex: number) => {
    setSelection((prev) => arrayMove(prev, fromIndex, toIndex));
  };

  // v1 ships without form-gated products, so FormAssociationPicker (the only UI that called this)
  // was removed from ProductListItem/ShopBlockEditor. Kept, uncalled, so the forms fast follow
  // can re-wire a picker back to this setter without rebuilding the selection-mutation logic.
  const setEntryFormId = (stripeProductId: string, formId: string | undefined) => {
    setSelection((prev) =>
      prev.map((entry) => (entry.stripeProductId === stripeProductId ? { ...entry, formId } : entry))
    );
  };

  const setEntryBadge = (stripeProductId: string, badge: string | undefined) => {
    setSelection((prev) =>
      prev.map((entry) => (entry.stripeProductId === stripeProductId ? { ...entry, badge } : entry))
    );
  };

  const setEntryRecommended = (stripeProductId: string, recommended: boolean) => {
    setSelection((prev) =>
      prev.map((entry) => (entry.stripeProductId === stripeProductId ? { ...entry, recommended } : entry))
    );
  };

  /**
   * Sets the limit from the raw text of the max-quantity input. Empty, non-numeric or below-1 input
   * clears it (back to the default of 1) rather than snapping to 1 mid-typing; values above the
   * checkout cap are lowered to it.
   */
  const setEntryMaxQuantity = (stripeProductId: string, inputValue: string) => {
    const parsed = Number.parseInt(inputValue, 10);
    const maxQuantity = Number.isNaN(parsed) || parsed < 1 ? undefined : Math.min(parsed, SHOP_MAX_QUANTITY);
    setSelection((prev) =>
      prev.map((entry) => (entry.stripeProductId === stripeProductId ? { ...entry, maxQuantity } : entry))
    );
  };

  /**
   * Whether this product may be bought more than one at a time. prepare_stripe_checkout rejects a
   * quantity above 1 when the resolved Product is recurring (stripeSubscriptionId) or has add-on
   * prices, so every one-time variant must resolve to a Product with neither. "unknown" while any
   * one-time variant's Product hasn't resolved — still loading, or not configured in the web app.
   */
  const getQuantitySupport = (entry: SelectedShopEntry): QuantitySupport => {
    const oneTimeProducts = entry.variants.flatMap((variant) =>
      variant.isSubscription ? [] : [productByStripePriceId.get(variant.stripePriceId)]
    );
    if (oneTimeProducts.length === 0) return "not-allowed";
    if (oneTimeProducts.some((product) => product?.stripeSubscriptionId || product?.additionalStripePriceIds?.length)) {
      return "not-allowed";
    }
    return oneTimeProducts.every((product) => product !== undefined) ? "allowed" : "unknown";
  };

  const setEntryCategory = (stripeProductId: string, category: string | undefined) => {
    setSelection((prev) =>
      prev.map((entry) => (entry.stripeProductId === stripeProductId ? { ...entry, category } : entry))
    );
  };

  const setEntryVisibility = (stripeProductId: string, condition: VisibilityCondition | undefined) => {
    setSelection((prev) =>
      prev.map((entry) =>
        entry.stripeProductId === stripeProductId
          ? { ...entry, visibility: condition ? { condition } : undefined }
          : entry
      )
    );
  };

  const unresolvedSelections = resolutionChecked
    ? selection.filter((entry) => entry.variants.some((v) => !productByStripePriceId.has(v.stripePriceId)))
    : [];

  const canSave = resolutionChecked && !resolutionLoading && unresolvedSelections.length === 0 && selection.length > 0;

  /** The shop block's snapshotted props.products entries — display data was already captured on each entry at selection time. */
  const buildSnapshot = (): ShopProduct[] | null => {
    if (!canSave) return null;
    return selection.map((entry) => ({
      stripeProductId: entry.stripeProductId,
      title: entry.title,
      description: entry.description,
      image: entry.image,
      formId: entry.formId,
      variants: entry.variants,
      badge: entry.badge,
      recommended: entry.recommended,
      maxQuantity:
        entry.maxQuantity && entry.maxQuantity > 1 && getQuantitySupport(entry) === "allowed"
          ? entry.maxQuantity
          : undefined,
      category: entry.category,
      visibility: entry.visibility,
    }));
  };

  return {
    stripeKey,
    setStripeKey,
    accounts,
    accountsLoading,
    accountsError,
    productsQuery,
    drillDownProductId,
    setDrillDownProductId,
    pricesQuery,
    selection,
    isPriceSelected,
    togglePrice,
    removeSelection,
    reorderSelection,
    setEntryFormId,
    setEntryBadge,
    setEntryRecommended,
    setEntryCategory,
    setEntryMaxQuantity,
    getQuantitySupport,
    setEntryVisibility,
    productByStripePriceId,
    unresolvedSelections,
    resolutionLoading,
    resolutionChecked,
    hasCheckedPriceId,
    canSave,
    buildSnapshot,
  };
};
